// Media settings: auto photo upload (and its type), auto video upload, and the
// upload path in dual-RC mode. Each is set in Pilot, read back from Pilot and
// remembered in localStorage (the media module is loaded with it on the next
// start). The controls stay visible whatever Pilot answers: the Flutter page
// replaced a control with nothing when Pilot's read-back was not a strict
// boolean (right_column_settings_widget.dart, `error: SizedBox.shrink()`,
// scratch 2026-10-20; T90 §4.7). A failure shows as a banner instead.
//
// Design system: form rows (116 px label, then the control); a setting that
// takes effect at once is a switch (CanvasSwitch), a choice is the 32 px
// dropdown well.

import type { Bridge, DjiReply } from './bridge';
import { reportError } from './report';
import { banner, el, field, formRow, icon } from './ui';

function remember(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode: Pilot still has the setting */
  }
}

function recall(key: string, fallback: string): string {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
}

/** Pilot's answer as a boolean: true / "true" / 1. */
export function asBool(r: DjiReply): boolean | undefined {
  if (r.code !== 0) return undefined;
  if (r.data === true || r.data === 'true' || r.data === 1) return true;
  if (r.data === false || r.data === 'false' || r.data === 0) return false;
  return undefined;
}

/** Pilot's answer as a small integer: 0 / "0" / 1 / "1". */
export function asInt(r: DjiReply): number | undefined {
  if (r.code !== 0 || r.data === null || r.data === undefined || r.data === '') return undefined;
  const n = Number(r.data);
  return Number.isInteger(n) ? n : undefined;
}

const UNCONFIRMED = 'DJI Pilot did not confirm the change. Load the Media module on the Modules tab, then set it again.';

/** A CanvasSwitch: green when on; set in Pilot, then read back. */
function toggle(key: string, set: (on: boolean) => DjiReply, get: () => DjiReply, warn: (t: string) => void, onChange?: (on: boolean) => void) {
  const sw = el('button', 'ac-switch');
  sw.type = 'button';
  sw.setAttribute('role', 'switch');
  let on = recall(key, 'false') === 'true';
  const paint = () => {
    sw.classList.toggle('is-on', on);
    sw.setAttribute('aria-checked', String(on));
  };
  paint();
  sw.addEventListener('click', () => {
    const want = !on;
    let got: boolean | undefined;
    try {
      set(want);
      got = asBool(get());
    } catch (e) {
      reportError(e, `setting ${key}`);
    }
    on = got ?? want;
    remember(key, String(on));
    warn(got === undefined ? UNCONFIRMED : '');
    paint();
    onChange?.(on);
  });
  return { sw, isOn: () => on };
}

/** A two-way choice as the dropdown well; set in Pilot, then read back. */
function choice(key: string, options: string[], fallback: number, set: (v: number) => DjiReply, get: () => DjiReply, warn: (t: string) => void) {
  const s = el('select');
  options.forEach((o, i) => s.append(new Option(o, String(i))));
  s.value = recall(key, String(fallback));
  s.addEventListener('change', () => {
    const want = Number(s.value);
    let got: number | undefined;
    try {
      set(want);
      got = asInt(get());
    } catch (e) {
      reportError(e, `setting ${key}`);
    }
    s.value = String(got ?? want);
    remember(key, s.value);
    warn(got === undefined ? UNCONFIRMED : '');
  });
  const well = field(s, 'ac-field--wide');
  well.append(icon('caret-down', 'caret'));
  return well;
}

export function settingsPanel(bridge: Bridge): HTMLElement {
  const box = el('div', 'col settings');
  const warning = banner('error');
  const warn = (t: string) => warning.set(t);

  const photoType = formRow(
    'Photo upload type',
    choice('autoUploadPhotoType', ['Original', 'Thumbnail'], 1, (v) => bridge.mediaSetUploadPhotoType(v), () => bridge.mediaGetUploadPhotoType(), warn),
  );
  const photo = toggle(
    'autoUploadPhoto',
    (on) => bridge.mediaSetAutoUploadPhoto(on),
    () => bridge.mediaGetAutoUploadPhoto(),
    warn,
    (on) => (photoType.hidden = !on),
  );
  photoType.hidden = !photo.isOn();
  const video = toggle('autoUploadVideo', (on) => bridge.mediaSetAutoUploadVideo(on), () => bridge.mediaGetAutoUploadVideo(), warn);
  // Dual-RC upload starts on this controller at every page start, whatever was
  // chosen before (operator, 2026-10-21): the controller the pilot holds uploads
  // unless they say otherwise for this session.
  remember('uploadPath', '0');
  try {
    bridge.mediaSetDownloadOwner(0);
  } catch (e) {
    reportError(e, 'setting uploadPath');
  }
  const owner = choice('uploadPath', ['This controller', 'The other controller'], 0, (v) => bridge.mediaSetDownloadOwner(v), () => bridge.mediaGetDownloadOwner(), warn);

  box.append(
    formRow('Auto photo upload', photo.sw),
    photoType,
    formRow('Auto video upload', video.sw),
    formRow('Dual-RC upload', owner),
    warning.el,
  );
  return box;
}
